/**
 * PSC API Client Service
 *
 * Handles all communication with the SPC backend.
 */

import type {
  RequirementType,
  QuestionnaireTemplate,
  QuestionnaireData,
  RequirementResponse,
  SaveResponseData,
  ServiceInfo,
  CreateRequirementRequest,
  PSCApiClient,
} from '../types';

/**
 * API Error class for structured error handling
 */
export class PSCApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public errorCode?: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'PSCApiError';
  }
}

interface ApiClientOptions {
  baseUrl: string;
  getAuthToken?: () => string | Promise<string>;
  timeout?: number;
}

// Service name to ID cache (shared across clients)
const serviceIdCache = new Map<string, number>();

/**
 * Create a PSC API client instance
 */
export function createPSCApiClient(options: ApiClientOptions): PSCApiClient {
  const { baseUrl, getAuthToken, timeout = 30000 } = options;
  // baseUrl is already /api/v1, so just use it directly
  const apiUrl = baseUrl;

  /**
   * Make an authenticated API request
   */
  async function request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${apiUrl}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    };

    // Add auth token if available
    if (getAuthToken) {
      try {
        const token = await getAuthToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.warn('Failed to get auth token:', error);
      }
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorData: unknown;
        try {
          errorData = await response.json();
        } catch {
          errorData = { message: response.statusText };
        }

        const errorMessage =
          (errorData as { detail?: string; message?: string })?.detail ||
          (errorData as { message?: string })?.message ||
          'API request failed';

        throw new PSCApiError(
          errorMessage,
          response.status,
          (errorData as { error_code?: string })?.error_code,
          errorData
        );
      }

      // Handle empty responses
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        return await response.json();
      }

      return undefined as T;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof PSCApiError) {
        throw error;
      }

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new PSCApiError('Request timeout', 408);
        }
        throw new PSCApiError(error.message || 'Network error', 0);
      }

      throw new PSCApiError('Unknown error occurred');
    }
  }

  /**
   * Resolve service name to numeric ID
   */
  async function resolveServiceId(serviceName: string): Promise<number> {
    // Check cache first
    if (serviceIdCache.has(serviceName)) {
      return serviceIdCache.get(serviceName)!;
    }

    // Fetch services from backend and populate cache
    try {
      const services = await request<Array<{ id: number; service_name: string }>>('/spc/services');
      for (const svc of services) {
        serviceIdCache.set(svc.service_name, svc.id);
      }

      if (serviceIdCache.has(serviceName)) {
        return serviceIdCache.get(serviceName)!;
      }
    } catch (error) {
      console.warn('Failed to fetch services for ID lookup:', error);
    }

    // Fallback: return 1 (default service) if not found
    console.warn(`Service "${serviceName}" not found, using default service ID 1`);
    return 1;
  }

  return {
    /**
     * Create a new requirement
     */
    async createRequirement(data: CreateRequirementRequest): Promise<RequirementResponse> {
      // Resolve service name to numeric ID
      const serviceId = await resolveServiceId(data.source_service_id);

      return request<RequirementResponse>('/requirements', {
        method: 'POST',
        body: JSON.stringify({
          requirement_type: data.requirement_type,
          source_service_id: serviceId,
          page_context: data.page_context,
          title: data.title,
          description: data.description,
          priority: data.priority,
          submitter_id: 1, // Default to admin user; should come from auth context in production
        }),
      });
    },

    /**
     * Get questionnaire template for a service and requirement type
     */
    async getTemplate(
      serviceId: string,
      type: RequirementType
    ): Promise<QuestionnaireTemplate> {
      return request<QuestionnaireTemplate>(
        `/templates?service=${encodeURIComponent(serviceId)}&type=${encodeURIComponent(type)}`
      );
    },

    /**
     * Save questionnaire data for a requirement
     */
    async saveQuestionnaireData(
      requirementId: string,
      data: QuestionnaireData,
      isComplete: boolean
    ): Promise<SaveResponseData> {
      return request<SaveResponseData>(
        `/requirements/by-req-id/${encodeURIComponent(requirementId)}/versions`,
        {
          method: 'PUT',
          body: JSON.stringify({
            questionnaire_data: data,
            is_complete: isComplete,
          }),
        }
      );
    },

    /**
     * Submit requirement for review
     */
    async submitForReview(requirementId: string): Promise<void> {
      return request<void>(
        `/requirements/by-req-id/${encodeURIComponent(requirementId)}/submit`,
        { method: 'POST' }
      );
    },

    /**
     * Get available services
     */
    async getServices(): Promise<ServiceInfo[]> {
      const services = await request<Array<{ id: number; service_name: string; service_display_name: string }>>('/spc/services');
      // Map to ServiceInfo format and populate cache
      return services.map(s => {
        serviceIdCache.set(s.service_name, s.id);
        return {
          id: s.id,
          name: s.service_name,
          display_name: s.service_display_name,
        };
      });
    },

    /**
     * Upload screenshots for a requirement
     */
    async uploadScreenshots(
      requirementId: string,
      screenshots: Array<{
        image_data: string;
        filename?: string;
        metadata?: {
          capture_type?: string;
          page_url?: string;
          viewport_size?: { width: number; height: number };
        };
      }>
    ): Promise<void> {
      // Upload screenshots one by one
      for (const screenshot of screenshots) {
        await request<unknown>(
          `/requirements/by-req-id/${encodeURIComponent(requirementId)}/screenshots`,
          {
            method: 'POST',
            body: JSON.stringify(screenshot),
          }
        );
      }
    },

    /**
     * Upload session recording for a requirement
     */
    async uploadRecording(
      requirementId: string,
      recording: {
        recording_data: string;
        metadata: {
          duration: number;
          event_count: number;
          start_time: string;
          end_time: string;
          original_size: number;
          compressed_size: number;
        };
      }
    ): Promise<void> {
      await request<unknown>(
        `/requirements/by-req-id/${encodeURIComponent(requirementId)}/recordings?uploaded_by=1`,
        {
          method: 'POST',
          body: JSON.stringify(recording),
        }
      );
    },
  };
}

/**
 * Create a mock API client for development/testing
 */
export function createMockPSCApiClient(): PSCApiClient {
  let requirementCounter = 1;

  return {
    async createRequirement(data: CreateRequirementRequest): Promise<RequirementResponse> {
      const id = requirementCounter++;
      return {
        requirement_id: `REQ-${Date.now()}-${id}`,
        id,
        title: data.title || '',
        requirement_type: data.requirement_type,
        source_service: {
          id: 1,
          name: data.source_service_id,
          display_name: data.source_service_id,
        },
        page_context: data.page_context,
        status: 'draft',
        submitter_id: 1,
        created_at: new Date().toISOString(),
        current_version: {
          version_number: 1,
          questionnaire_data: {},
          created_at: new Date().toISOString(),
        },
      };
    },

    async getTemplate(
      serviceId: string,
      type: RequirementType
    ): Promise<QuestionnaireTemplate> {
      return {
        template_id: 1,
        template_name: `${type} Template`,
        service: {
          id: 1,
          name: serviceId,
          display_name: serviceId,
        },
        version: '1.0.0',
        sections: [
          {
            id: 'basic_info',
            title: 'Basic Information',
            description: 'Provide basic details about this requirement',
            questions: [
              {
                id: 'title',
                type: 'text',
                label: 'Title',
                required: true,
                validation: { min_length: 3, max_length: 200 },
                placeholder: 'Enter a descriptive title',
              },
              {
                id: 'description',
                type: 'textarea',
                label: 'Description',
                required: true,
                validation: { min_length: 10, max_length: 2000 },
                placeholder: 'Describe the requirement in detail',
                help_text: 'Be as specific as possible',
              },
              {
                id: 'priority',
                type: 'select',
                label: 'Priority',
                required: true,
                options: ['low', 'medium', 'high', 'critical'],
              },
            ],
          },
          {
            id: 'details',
            title: 'Additional Details',
            questions: [
              {
                id: 'steps_to_reproduce',
                type: 'textarea',
                label: 'Steps to Reproduce (for bugs)',
                required: false,
                placeholder: '1. Go to...\n2. Click on...\n3. See error',
              },
              {
                id: 'expected_behavior',
                type: 'textarea',
                label: 'Expected Behavior',
                required: false,
                placeholder: 'What should happen?',
              },
              {
                id: 'actual_behavior',
                type: 'textarea',
                label: 'Actual Behavior',
                required: false,
                placeholder: 'What actually happens?',
              },
            ],
          },
        ],
      };
    },

    async saveQuestionnaireData(
      requirementId: string,
      _data: QuestionnaireData,
      _isComplete: boolean
    ): Promise<SaveResponseData> {
      return {
        requirement_id: requirementId,
        version_number: 1,
        last_saved_at: new Date().toISOString(),
        validation_errors: [],
      };
    },

    async submitForReview(_requirementId: string): Promise<void> {
      // Mock submission
    },

    async getServices(): Promise<ServiceInfo[]> {
      return [
        { id: 1, name: 'treasury', display_name: 'Treasury' },
        { id: 2, name: 'risk', display_name: 'Risk' },
        { id: 3, name: 'market-data', display_name: 'Market Data' },
        { id: 4, name: 'pricing', display_name: 'Pricing' },
        { id: 5, name: 'alm', display_name: 'ALM' },
      ];
    },

    async uploadScreenshots(
      _requirementId: string,
      _screenshots: Array<{
        image_data: string;
        filename?: string;
        metadata?: {
          capture_type?: string;
          page_url?: string;
          viewport_size?: { width: number; height: number };
        };
      }>
    ): Promise<void> {
      // Mock screenshot upload
    },

    async uploadRecording(
      _requirementId: string,
      _recording: {
        recording_data: string;
        metadata: {
          duration: number;
          event_count: number;
          start_time: string;
          end_time: string;
          original_size: number;
          compressed_size: number;
        };
      }
    ): Promise<void> {
      // Mock recording upload
    },
  };
}

export type { PSCApiClient };
